import { getFunctions, httpsCallable } from 'firebase/functions'

import db, { getOfficeSubscription } from '../db/db'
import Database from '../types/Database'
import { determineUserRole } from '../utils/profileHelpers'

import ErrorHandler from './ErrorHandler'

/**
 * AdminService
 *
 * Service class for administrative operations.
 * Handles user management, system statistics, office configuration, and audit logging.
 *
 * Requirements: 3.1, 3.3, 4.1, 4.2, 4.3, 4.4, 7.1, 7.2, 7.3, 10.2, 10.3, 10.5, 10.7
 */
export class AdminService {
  /**
   * Get all users from the system
   *
   * Requirements:
   * - 3.1: Display list of all users
   * - 6.1, 6.2, 6.3, 6.4: Display user details
   */
  static async getAllUsers(): Promise<Database.User[]> {
    try {
      ErrorHandler.logInfo(
        'Fetching all users',
        {},
        { component: 'AdminService', action: 'getAllUsers' }
      )

      const users = await db.query({
        collection: 'users',
        limit: 10000 // High limit to get all users
      })

      ErrorHandler.logInfo(
        'Successfully fetched all users',
        { userCount: users.length },
        { component: 'AdminService', action: 'getAllUsers' }
      )

      return users
    } catch (error) {
      const errorInstance =
        error instanceof Error ? error : new Error(String(error))

      ErrorHandler.logSystemError('Error fetching all users', errorInstance, {
        component: 'AdminService',
        action: 'getAllUsers'
      })

      throw new Error(`Failed to fetch users: ${errorInstance.message}`)
    }
  }

  /**
   * Get detailed information about an office including subscription, owner, and access list
   */
  static async getOfficeSubscriptionDetails(
    officeId: string
  ): Promise<{
    subscription: Database.Subscription | null
    ownerEmail: string | null
  }> {
    try {
      ErrorHandler.logInfo(
        'Fetching office subscription details',
        { officeId },
        { component: 'AdminService', action: 'getOfficeSubscriptionDetails' }
      )

      const [subscription, offices] = await Promise.all([
        getOfficeSubscription(officeId),
        db.query({
          collection: 'offices',
          where: ['oid', '==', officeId],
          limit: 1
        })
      ])

      let ownerEmail: string | null = null
      if (offices.length > 0 && offices[0].uid) {
        const ownerUser = await db.getById('users', offices[0].uid)
        if (ownerUser) {
          ownerEmail = ownerUser.email || null
        }
      }

      return { subscription, ownerEmail }
    } catch (error) {
      const errorInstance =
        error instanceof Error ? error : new Error(String(error))

      ErrorHandler.logSystemError(
        'Error fetching office subscription details',
        errorInstance,
        {
          component: 'AdminService',
          action: 'getOfficeSubscriptionDetails'
        }
      )

      throw new Error(
        `Failed to fetch office details: ${errorInstance.message}`
      )
    }
  }

  /**
   * Get the access list for an office with user roles
   */
  static async getOfficeAccessList(
    officeId: string,
    office: Database.Office
  ): Promise<
    Array<{
      user: Database.User
      role: 'owner' | 'manager' | 'member'
    }>
  > {
    try {
      ErrorHandler.logInfo(
        'Fetching office access list',
        { officeId },
        { component: 'AdminService', action: 'getOfficeAccessList' }
      )

      const [users, officeManagers] = await Promise.all([
        db.query({
          collection: 'users',
          where: ['officeids', 'array-contains', officeId],
          limit: 10000
        }),
        db.query({
          collection: 'officeManagers',
          where: ['officeId', '==', officeId],
          limit: 10000
        })
      ])

      const accessList = users.map(user => ({
        user,
        role: determineUserRole(office, user, officeManagers)
      }))

      // Sort: owners first, then managers, then members
      const roleOrder = { owner: 0, manager: 1, member: 2 }
      accessList.sort((a, b) => roleOrder[a.role] - roleOrder[b.role])

      return accessList
    } catch (error) {
      const errorInstance =
        error instanceof Error ? error : new Error(String(error))

      ErrorHandler.logSystemError(
        'Error fetching office access list',
        errorInstance,
        {
          component: 'AdminService',
          action: 'getOfficeAccessList'
        }
      )

      throw new Error(
        `Failed to fetch office access list: ${errorInstance.message}`
      )
    }
  }

  /**
   * Get offices and roles for a specific user
   * Fetches offices that the user belongs to and determines their role in each
   */
  static async getUserOfficesWithRoles(
    user: Database.User
  ): Promise<
    Array<{
      officeName: string
      role: 'owner' | 'manager' | 'member'
    }>
  > {
    try {
      const officeIds = user.officeids
      if (!officeIds || officeIds.length === 0) {
        return []
      }

      // Firestore 'in' queries limited to 30 values - chunk if needed
      const chunkSize = 30
      const chunks: string[][] = []
      for (let i = 0; i < officeIds.length; i += chunkSize) {
        chunks.push(officeIds.slice(i, i + chunkSize))
      }

      const allOffices: Database.Office[] = []
      for (const chunk of chunks) {
        const offices = await db.query({
          collection: 'offices',
          where: ['oid', 'in', chunk]
        })
        allOffices.push(...offices)
      }

      if (allOffices.length === 0) {
        return []
      }

      // Fetch office managers for all these offices
      const officeManagerPromises = allOffices.map(office =>
        db.query({
          collection: 'officeManagers',
          where: ['officeId', '==', office.oid],
          limit: 10000
        })
      )
      const officeManagerResults = await Promise.all(officeManagerPromises)
      const allManagers = officeManagerResults.flat()

      return allOffices.map(office => ({
        officeName: office.name || 'Unnamed Office',
        role: determineUserRole(office, user, allManagers)
      }))
    } catch (error) {
      const errorInstance =
        error instanceof Error ? error : new Error(String(error))

      ErrorHandler.logSystemError(
        'Error fetching user offices with roles',
        errorInstance,
        {
          component: 'AdminService',
          action: 'getUserOfficesWithRoles'
        }
      )

      return []
    }
  }

  /**
   * Update a user's admin role
   *
   * Requirements:
   * - 1.3: Allow administrators to promote regular users to administrators
   * - 1.4: Allow administrators to demote other administrators to regular users
   */
  static async updateUserRole(userId: string, isAdmin: boolean): Promise<void> {
    try {
      if (!userId) {
        throw new Error('User ID is required')
      }

      ErrorHandler.logInfo(
        'Updating user role',
        { userId, isAdmin },
        { component: 'AdminService', action: 'updateUserRole' }
      )

      await db.updateItem('users', userId, {
        isAdmin
      })

      ErrorHandler.logInfo(
        'Successfully updated user role',
        { userId, isAdmin },
        { component: 'AdminService', action: 'updateUserRole' }
      )
    } catch (error) {
      const errorInstance =
        error instanceof Error ? error : new Error(String(error))

      ErrorHandler.logSystemError('Error updating user role', errorInstance, {
        component: 'AdminService',
        action: 'updateUserRole'
      })

      throw new Error(`Failed to update user role: ${errorInstance.message}`)
    }
  }

  /**
   * Delete a user account by calling the Cloud Function
   *
   * Requirements:
   * - 3.3: Delete user document from Firestore
   * - 3.4: Delete user account from Firebase Auth
   * - 3.8: Remove user from all office memberships
   * - 3.9: Delete all bookings created by the user
   */
  static async deleteUser(userId: string): Promise<void> {
    try {
      if (!userId) {
        throw new Error('User ID is required')
      }

      ErrorHandler.logInfo(
        'Deleting user account',
        { userId },
        { component: 'AdminService', action: 'deleteUser' }
      )

      const functions = getFunctions()
      const deleteUserAccount = httpsCallable(functions, 'deleteUserAccount')

      const result = await deleteUserAccount({ userId })
      const data = result.data as {
        success: boolean
        message: string
        partialDeletion?: boolean
      }

      if (!data.success) {
        throw new Error(data.message)
      }

      if (data.partialDeletion) {
        ErrorHandler.logWarning(
          'Partial user deletion occurred',
          { userId, message: data.message },
          { component: 'AdminService', action: 'deleteUser' }
        )
        throw new Error(data.message)
      }

      ErrorHandler.logInfo(
        'Successfully deleted user account',
        { userId },
        { component: 'AdminService', action: 'deleteUser' }
      )
    } catch (error) {
      const errorInstance =
        error instanceof Error ? error : new Error(String(error))

      ErrorHandler.logSystemError('Error deleting user', errorInstance, {
        component: 'AdminService',
        action: 'deleteUser'
      })

      throw new Error(`Failed to delete user: ${errorInstance.message}`)
    }
  }

  /**
   * Get system-wide statistics
   *
   * Requirements:
   * - 4.1: Display total count of users
   * - 4.2: Display total count of offices
   * - 4.3: Display total count of desks
   * - 4.4: Display count of offices grouped by subscription tier
   */
  static async getSystemStatistics(): Promise<Database.SystemStatistics> {
    try {
      ErrorHandler.logInfo(
        'Fetching system statistics',
        {},
        { component: 'AdminService', action: 'getSystemStatistics' }
      )

      // Query all collections in parallel
      const [users, offices, desks] = await Promise.all([
        db.query({ collection: 'users', limit: 10000 }),
        db.query({ collection: 'offices', limit: 10000 }),
        db.query({ collection: 'desks', limit: 10000 })
      ])

      // Count offices by tier
      const officesByTier = offices.reduce(
        (acc, office) => {
          const tier = office.subscriptionTier || 'free'
          acc[tier] = (acc[tier] || 0) + 1
          return acc
        },
        {
          free: 0,
          small: 0,
          medium: 0,
          large: 0
        } as Record<Database.SubscriptionTier, number>
      )

      const statistics: Database.SystemStatistics = {
        totalUsers: users.length,
        totalOffices: offices.length,
        totalDesks: desks.length,
        officesByTier,
        lastUpdated: new Date()
      }

      ErrorHandler.logInfo(
        'Successfully fetched system statistics',
        statistics,
        { component: 'AdminService', action: 'getSystemStatistics' }
      )

      return statistics
    } catch (error) {
      const errorInstance =
        error instanceof Error ? error : new Error(String(error))

      ErrorHandler.logSystemError(
        'Error fetching system statistics',
        errorInstance,
        {
          component: 'AdminService',
          action: 'getSystemStatistics'
        }
      )

      throw new Error(
        `Failed to fetch system statistics: ${errorInstance.message}`
      )
    }
  }

  /**
   * Update office desk capacity
   *
   * Requirements:
   * - 10.2: Allow administrators to modify desk capacity
   * - 10.3: Validate capacity is a positive integer
   * - 10.4: Update office document in Firestore
   */
  static async updateOfficeCapacity(
    officeId: string,
    capacity: number
  ): Promise<void> {
    try {
      if (!officeId) {
        throw new Error('Office ID is required')
      }

      // Validate capacity is a positive integer
      if (!Number.isInteger(capacity) || capacity <= 0) {
        throw new Error('Capacity must be a positive integer')
      }

      ErrorHandler.logInfo(
        'Updating office capacity',
        { officeId, capacity },
        { component: 'AdminService', action: 'updateOfficeCapacity' }
      )

      await db.updateItem('offices', officeId, {
        userCapacity: capacity
      })

      ErrorHandler.logInfo(
        'Successfully updated office capacity',
        { officeId, capacity },
        { component: 'AdminService', action: 'updateOfficeCapacity' }
      )
    } catch (error) {
      const errorInstance =
        error instanceof Error ? error : new Error(String(error))

      ErrorHandler.logSystemError(
        'Error updating office capacity',
        errorInstance,
        {
          component: 'AdminService',
          action: 'updateOfficeCapacity'
        }
      )

      throw new Error(
        `Failed to update office capacity: ${errorInstance.message}`
      )
    }
  }

  /**
   * Update office subscription tier
   *
   * Requirements:
   * - 10.5: Allow administrators to change subscription tier
   * - 10.6: Display available tier options
   * - 10.7: Update office document in Firestore
   */
  static async updateOfficeTier(
    officeId: string,
    tier: Database.SubscriptionTier
  ): Promise<void> {
    try {
      if (!officeId) {
        throw new Error('Office ID is required')
      }

      // Validate tier is one of the valid options
      const validTiers: Database.SubscriptionTier[] = [
        'free',
        'small',
        'medium',
        'large'
      ]
      if (!validTiers.includes(tier)) {
        throw new Error(
          `Invalid tier: ${tier}. Must be one of: ${validTiers.join(', ')}`
        )
      }

      ErrorHandler.logInfo(
        'Updating office tier',
        { officeId, tier },
        { component: 'AdminService', action: 'updateOfficeTier' }
      )

      await db.updateItem('offices', officeId, {
        subscriptionTier: tier
      })

      ErrorHandler.logInfo(
        'Successfully updated office tier',
        { officeId, tier },
        { component: 'AdminService', action: 'updateOfficeTier' }
      )
    } catch (error) {
      const errorInstance =
        error instanceof Error ? error : new Error(String(error))

      ErrorHandler.logSystemError('Error updating office tier', errorInstance, {
        component: 'AdminService',
        action: 'updateOfficeTier'
      })

      throw new Error(`Failed to update office tier: ${errorInstance.message}`)
    }
  }

  /**
   * Toggle the soft-delete flag on an office
   *
   * Allows administrators to mark an office as deleted or restore a deleted office.
   * When marking as deleted, sets isDeleted=true, deletedAt, and deletedBy.
   * When restoring, clears isDeleted, deletedAt, and deletedBy.
   *
   * @param officeId - The office ID to toggle
   * @param isDeleted - Whether to mark as deleted (true) or restore (false)
   * @param adminId - The admin performing the action
   */
  static async toggleOfficeDeleted(
    officeId: string,
    isDeleted: boolean,
    adminId: string
  ): Promise<void> {
    try {
      if (!officeId) {
        throw new Error('Office ID is required')
      }

      if (!adminId) {
        throw new Error('Admin ID is required')
      }

      ErrorHandler.logInfo(
        'Toggling office deleted status',
        { officeId, isDeleted, adminId },
        { component: 'AdminService', action: 'toggleOfficeDeleted' }
      )

      const updateData: Record<string, any> = {
        isDeleted,
        updatedAt: new Date()
      }

      if (isDeleted) {
        updateData.deletedAt = new Date()
        updateData.deletedBy = adminId
      } else {
        // Clear deletion metadata when restoring
        updateData.deletedAt = null
        updateData.deletedBy = null
      }

      await db.updateItem('offices', officeId, updateData)

      ErrorHandler.logInfo(
        'Successfully toggled office deleted status',
        { officeId, isDeleted },
        { component: 'AdminService', action: 'toggleOfficeDeleted' }
      )
    } catch (error) {
      const errorInstance =
        error instanceof Error ? error : new Error(String(error))

      ErrorHandler.logSystemError(
        'Error toggling office deleted status',
        errorInstance,
        {
          component: 'AdminService',
          action: 'toggleOfficeDeleted'
        }
      )

      throw new Error(
        `Failed to ${isDeleted ? 'delete' : 'restore'} office: ${errorInstance.message}`
      )
    }
  }

  /**
   * Log an administrative action to the audit trail
   *
   * Requirements:
   * - 7.1: Log user deletion actions
   * - 7.2: Log user promotion actions
   * - 7.3: Log user demotion actions
   * - 10.11: Log office configuration changes
   */
  static async logAdminAction(
    adminId: string,
    adminEmail: string,
    action: Database.AuditLogEntry['action'],
    targetId: string,
    targetEmail: string | undefined,
    details: Database.AuditLogEntry['details']
  ): Promise<void> {
    try {
      ErrorHandler.logInfo(
        'Creating audit log entry',
        { adminId, action, targetId },
        { component: 'AdminService', action: 'logAdminAction' }
      )

      await db.addItem('auditLogs', {
        adminId,
        adminEmail,
        action,
        targetId,
        targetEmail,
        timestamp: new Date(),
        details
      })

      ErrorHandler.logInfo(
        'Successfully created audit log entry',
        { adminId, action, targetId },
        { component: 'AdminService', action: 'logAdminAction' }
      )
    } catch (error) {
      const errorInstance =
        error instanceof Error ? error : new Error(String(error))

      ErrorHandler.logSystemError(
        'Error creating audit log entry',
        errorInstance,
        {
          component: 'AdminService',
          action: 'logAdminAction'
        }
      )

      // Don't throw error for audit log failures - log but continue
      ErrorHandler.logWarning(
        'Failed to create audit log entry, continuing operation',
        { error: errorInstance.message },
        { component: 'AdminService', action: 'logAdminAction' }
      )
    }
  }

  /**
   * Get recent audit log entries
   *
   * Requirements:
   * - 7.5: Display recent administrative actions
   */
  static async getRecentAuditLogs(
    limit: number = 50
  ): Promise<Database.AuditLogEntry[]> {
    try {
      ErrorHandler.logInfo(
        'Fetching recent audit logs',
        { limit },
        { component: 'AdminService', action: 'getRecentAuditLogs' }
      )

      const logs = await db.query({
        collection: 'auditLogs',
        orderBy: ['timestamp', 'desc'],
        limit
      })

      ErrorHandler.logInfo(
        'Successfully fetched recent audit logs',
        { logCount: logs.length },
        { component: 'AdminService', action: 'getRecentAuditLogs' }
      )

      return logs
    } catch (error) {
      const errorInstance =
        error instanceof Error ? error : new Error(String(error))

      const errorMessage = errorInstance.message.toLowerCase()
      const isPermissionError =
        errorMessage.includes('permission') ||
        errorMessage.includes('insufficient') ||
        errorMessage.includes('unauthorized')

      ErrorHandler.logSystemError(
        'Error fetching recent audit logs',
        errorInstance,
        {
          component: 'AdminService',
          action: 'getRecentAuditLogs'
        }
      )

      if (isPermissionError) {
        throw new Error(
          'Unable to fetch audit logs due to insufficient permissions. ' +
            'Please ensure you are signed in as an admin and that Firestore ' +
            'security rules have been deployed with admin write access for the auditLogs collection.'
        )
      }

      throw new Error(
        `Failed to fetch recent audit logs: ${errorInstance.message}`
      )
    }
  }
}

export default AdminService
